import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RefreshCw, MessageCircle, Facebook, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { pub } from "@/lib/api";
import content from "@/interactive-content.json";

const CARDS = content.cards.items;
const L = ["en", "ro", "es", "fr", "it", "pt"];
const S = {
  tap: ["Tap a card to turn it over", "Atinge o carte ca să o întorci", "Toca una carta para darle la vuelta", "Touchez une carte pour la retourner", "Tocca una carta per girarla", "Toca numa carta para a virar"],
  full: ["Open the full reflection", "Deschide reflecția completă", "Abrir la reflexión completa", "Ouvrir la réflexion complète", "Apri la riflessione completa", "Abrir a reflexão completa"],
  again: ["Shuffle again", "Amestecă din nou", "Barajar de nuevo", "Mélanger à nouveau", "Mescola di nuovo", "Baralhar de novo"],
  give: ["What you give with love returns to you.", "Ce dăruiești cu iubire se întoarce la tine.", "Lo que das con amor vuelve a ti.", "Ce que vous donnez avec amour vous revient.", "Ciò che doni con amore ritorna a te.", "O que dás com amor volta para ti."],
  club: ["Aya Club levels", "Nivelurile Aya Club", "Niveles de Aya Club", "Niveaux Aya Club", "Livelli Aya Club", "Níveis do Aya Club"],
  wkH: ["Posts from my soul", "Postările sufletului meu", "Publicaciones de mi alma", "Publications de mon âme", "I post della mia anima", "Publicações da minha alma"],
  pLive: ["Andreea is LIVE now", "Andreea e LIVE acum", "Andreea está EN DIRECTO", "Andreea est EN DIRECT", "Andreea è IN DIRETTA", "Andreea está EM DIRETO"],
  pSession: ["In a private session", "În ședință privată", "En sesión privada", "En séance privée", "In sessione privata", "Em sessão privada"],
  pOffline: ["Offline", "Offline", "Desconectada", "Hors ligne", "Offline", "Offline"],
  pWa: ["Write on WhatsApp", "Scrie-i pe WhatsApp", "Escríbele por WhatsApp", "Écrivez sur WhatsApp", "Scrivile su WhatsApp", "Escreve no WhatsApp"],
  pWatch: ["Watch the live", "Intră la live", "Ver el directo", "Voir le direct", "Guarda la diretta", "Ver o direto"],  wkPost: ["Post", "Postarea", "Publicación", "Publication", "Post", "Publicação"],
  wkMore: ["Read the whole post", "Citește toată postarea", "Leer toda la publicación", "Lire toute la publication", "Leggi tutto il post", "Ler toda a publicação"],
  wkLess: ["Show less", "Arată mai puțin", "Mostrar menos", "Afficher moins", "Mostra meno", "Mostrar menos"],
  wkShare: ["Share this post", "Trimite postarea", "Comparte la publicación", "Partager la publication", "Condividi il post", "Partilhar a publicação"],
  wkCopy: ["Copy link", "Copiază linkul", "Copiar enlace", "Copier le lien", "Copia link", "Copiar link"],
  wkCopied: ["Link copied", "Link copiat", "Enlace copiado", "Lien copié", "Link copiato", "Link copiado"],
  wkPrev: ["All posts", "Toate postările", "Todas las publicaciones", "Toutes les publications", "Tutti i post", "Todas as publicações"],
};
export const wkNo = (n) => `#${String(n).padStart(4, "0")}`;
const imgSrc = (u) => (u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function PostBody({ p, children }) {
  const s = useS();
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(true);
  const bodyRef = useRef(null);
  useEffect(() => { setOpen(false); if (bodyRef.current) setLong(bodyRef.current.scrollHeight > 440); }, [p.id]);
  const paras = String(p.body || "").split("|");
  return (
    <div className="wrap grid md:grid-cols-[minmax(0,360px)_1fr] gap-10 lg:gap-16 items-start">
      <img src={imgSrc(p.image_url)} alt={p.title} loading="lazy" width="360" height="640"
        className="w-full max-w-[360px] mx-auto aspect-[9/16] object-cover rounded-3xl border-2 border-[var(--gold)] shadow-[0_30px_60px_-30px_rgba(0,0,0,.6)]" data-testid="weekly-image" />
      <div>
        <p className="eyebrow">{s("wkH")} · <span data-testid="weekly-number">{s("wkPost")} {wkNo(p.number)}</span></p>
        <h2 data-testid="weekly-title">{p.title}</h2>
        <div ref={bodyRef} className={`grid gap-4 mt-6 text-[17px] leading-[1.8] ${open || !long ? "" : "max-h-[420px] overflow-hidden [mask-image:linear-gradient(180deg,#000_70%,transparent)]"}`} data-testid="weekly-body">
          {paras.map((t, k) => <p key={k}>{t}</p>)}
        </div>
        {long && <button className="btn btn-ghost btn-sm mt-4" onClick={() => setOpen(!open)} data-testid="weekly-toggle">{open ? s("wkLess") : s("wkMore")}</button>}
        <ShareRow post={p} />
        {children}
      </div>
    </div>
  );
}

export function WeeklyPost() {
  const s = useS();
  const [posts, setPosts] = useState([]);
  const [idx, setIdx] = useState(0);
  const topRef = useRef(null);
  const [qs] = useSearchParams();
  const want = Number(qs.get("post"));
  useEffect(() => {
    pub.get("/public/weekly-posts").then((r) => {
      setPosts(r.data);
      const k = r.data.findIndex((q) => q.number === want);
      if (k >= 0) { setIdx(k); setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth" }), 300); }
    }).catch(() => {});
  }, [want]);
  const p = posts[idx];
  if (!p) return null;
  return (
    <section ref={topRef} className="section bg-violet-wash scroll-mt-20" data-testid="weekly-post">
      <PostBody p={p}>
        {posts.length > 1 && <div className="mt-8"><p className="text-sm font-semibold mb-2">{s("wkPrev")}</p><div className="flex flex-wrap gap-2">
          {posts.map((q, k) => <button key={q.id} className="chip" aria-pressed={k === idx} onClick={() => { setIdx(k); topRef.current?.scrollIntoView({ behavior: "smooth" }); }} data-testid={`weekly-pick-${q.number}`}>{wkNo(q.number)}</button>)}</div></div>}
      </PostBody>
    </section>
  );
}

function ShareRow({ post }) {
  const s = useS();
  const { locale } = useT();
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/soul-post/${post.number}?l=${locale}`;
  const text = `${post.title} — ${s("wkH")} ${wkNo(post.number)}`;
  const e = encodeURIComponent;
  const copy = () => navigator.clipboard?.writeText(shareUrl).then(() => toast.success(s("wkCopied"))).catch(() => {});
  return (
    <div className="mt-8 pt-6 border-t border-[var(--line)]" data-testid="weekly-share">
      <p className="text-sm font-semibold mb-3">{s("wkShare")}</p>
      <div className="flex flex-wrap gap-2">
        <a className="btn btn-forest btn-sm" href={`https://wa.me/?text=${e(`${text}\n${shareUrl}`)}`} target="_blank" rel="noopener noreferrer" data-testid="weekly-share-whatsapp"><MessageCircle size={15} /> WhatsApp</a>
        <a className="btn btn-forest btn-sm" href={`https://www.facebook.com/sharer/sharer.php?u=${e(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="weekly-share-facebook"><Facebook size={15} /> Facebook</a>
        <button type="button" className="btn btn-ghost btn-sm" onClick={copy} data-testid="weekly-share-copy"><Link2 size={15} /> {s("wkCopy")}</button>
      </div>
    </div>
  );
}

export function SoulPostsPage() {
  const s = useS();
  const [posts, setPosts] = useState(null);
  const [qs] = useSearchParams();
  const want = qs.get("post");
  useEffect(() => { pub.get("/public/weekly-posts").then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);
  useEffect(() => {
    if (posts && want) { setTimeout(() => document.getElementById(`post-${want}`)?.scrollIntoView({ behavior: "smooth" }), 300); }
  }, [posts, want]);
  return (
    <div className="pt-6" data-testid="soul-posts-page">
      <div className="wrap pt-10"><h1 className="text-4xl sm:text-5xl">{s("wkH")}</h1></div>
      {(posts || []).map((p, k) => (
        <section key={p.id} id={`post-${p.number}`} className={`section scroll-mt-20 ${k % 2 ? "" : "bg-violet-wash"}`} data-testid={`soul-post-${p.number}`}>
          <PostBody p={p} />
        </section>))}
    </div>
  );
}

const DOT = { live: "bg-red-500 animate-pulse", session: "bg-amber-400", offline: "bg-gray-400" };
export function PresenceBadge() {
  const s = useS();
  const [p, setP] = useState(null);
  useEffect(() => {
    const load = () => pub.get("/public/presence").then((r) => setP(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  if (!p) return null;
  const label = { live: s("pLive"), session: s("pSession"), offline: s("pOffline") }[p.status];
  const href = p.status === "live" ? p.live_url : p.whatsapp;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid="presence-badge" data-status={p.status}
      className="dock-btn flex items-center gap-2 min-h-[44px] rounded-full pl-3 pr-4 bg-[var(--paper)] text-[var(--ink)] border border-[var(--gold)] shadow-lg text-xs font-semibold max-w-[78vw]">
      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${DOT[p.status]}`} aria-hidden="true" />
      <span className="truncate">{label} · <span className="text-[var(--amethyst)]">{p.status === "live" ? s("pWatch") : s("pWa")}</span></span>
    </a>
  );
}

export function useS() {
  const { locale } = useT();
  const i = Math.max(0, L.indexOf(locale));
  return (k) => S[k][i] || S[k][0];
}

const AV_COLORS = ["emerald", "violet", "rose", "indigo", "terracotta"];
const AV_SIGNS = ["eye", "sun", "moon", "star", "leaf", "heart", "wing", "portal", "butterfly", "crystal"];
export const avatarFor = (key) => {
  const n = [...String(key)].reduce((a, c) => (a * 31 + c.charCodeAt(0)) % 50, 7);
  return `/assets/avatars/${String(n + 1).padStart(2, "0")}-${AV_COLORS[Math.floor(n / 10)]}-${AV_SIGNS[n % 10]}.svg`;
};

const draw3 = () => {
  const ids = [...CARDS.keys()];
  for (let i = ids.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [ids[i], ids[j]] = [ids[j], ids[i]]; }
  return ids.slice(0, 3).map((k) => CARDS[k]);
};

function FlipCard({ card, open, onOpen, k }) {
  const { locale } = useT();
  const c = card.content[locale] || card.content.en;
  return (
    <button type="button" onClick={onOpen} aria-pressed={open} aria-label={open ? c.title : `Card ${k + 1}`}
      className={`flip-card ${open ? "is-open" : ""}`} style={{ "--tilt": `${(k - 1) * 6}deg` }} data-testid={`flip-card-${k}`}>
      <span className="flip-inner">
        <span className="flip-face flip-back"><img src="/assets/logo-main-192.png" alt="" aria-hidden="true" className="w-20 opacity-90" /></span>
        <span className="flip-face flip-front">
          <span className="eyebrow !text-[9px] sm:!text-[10px]">{card.id}</span>
          <span className="serif text-sm sm:text-xl leading-tight mt-1 sm:mt-2">{c.title}</span>
          <span className="text-[10px] sm:text-xs mt-2 sm:mt-3 leading-snug sm:leading-relaxed text-[var(--muted)] line-clamp-4 sm:line-clamp-5">{c.reflection}</span>
        </span>
      </span>
    </button>
  );
}

export function ThreeCards() {
  const s = useS();
  const { lp } = useT();
  const [cards, setCards] = useState(draw3);
  const [open, setOpen] = useState([false, false, false]);
  const reshuffle = () => { setOpen([false, false, false]); setTimeout(() => setCards(draw3()), 450); };
  return (
    <div className="mt-10 grid justify-items-center gap-6" data-testid="three-cards">
      <p className="text-sm text-[var(--muted)]">{s("tap")}</p>
      <div className="flex gap-3 sm:gap-6 justify-center">{cards.map((c, k) => (
        <FlipCard key={`${c.id}-${k}`} card={c} k={k} open={open[k]} onOpen={() => setOpen((o) => o.map((v, j) => (j === k ? !v : v)))} />))}</div>
      {open.some(Boolean) && <div className="flex flex-wrap gap-3 justify-center">
        <Link to={lp("/love-tarot")} className="btn btn-ghost btn-sm" data-testid="three-cards-full">{s("full")}</Link>
        <button className="btn btn-ghost btn-sm" onClick={reshuffle} data-testid="three-cards-shuffle"><RefreshCw size={14} aria-hidden="true" /> {s("again")}</button>
      </div>}
    </div>
  );
}

export function GiveBack() {
  const s = useS();
  return (
    <div className="flex items-center gap-4 mt-6" data-testid="give-back">
      <svg viewBox="0 0 64 64" className="w-16 h-16 shrink-0 text-[var(--gold)]" aria-hidden="true">
        <g className="giveback-spin" style={{ transformOrigin: "32px 32px" }} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M32 6a26 26 0 0 1 24.5 17.5" /><path d="M56 15v9h-9" />
          <path d="M32 58A26 26 0 0 1 7.5 40.5" /><path d="M8 49v-9h9" />
        </g>
        <path d="M32 44s-11-6.6-11-14a6 6 0 0 1 11-3.3A6 6 0 0 1 43 30c0 7.4-11 14-11 14z" fill="var(--amethyst)" opacity=".85" />
      </svg>
      <p className="serif text-xl">{s("give")}</p>
    </div>
  );
}

const CLUB = [["cooper", "Aya Cooper"], ["gold", "Aya Gold"], ["soul", "Aya Soul"]];
export function ClubCards() {
  const s = useS();
  const { lp } = useT();
  return (
    <div className="md:col-span-3 grid sm:grid-cols-3 gap-6" data-testid="club-cards">
      <h2 className="sm:col-span-3">{s("club")}</h2>
      {CLUB.map(([k, n]) => (
        <Link key={k} to={lp("/club")} className="block rounded-3xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 shadow-[0_20px_50px_-30px_rgba(0,0,0,.5)]" data-testid={`club-card-${k}`}>
          <img src={`/assets/club/aya-${k}.webp`} alt={n} loading="lazy" width="960" height="1140" className="w-full h-auto" />
        </Link>))}
    </div>
  );
}

// Owner-supplied YouTube thumbnails (m03-m11 landscape videos, m12-m18 vertical Shorts)
const MOSAIC = ["m03", "m12", "m04", "m05", "m13", "m06", "m14", "m07", "m08", "m15", "m09", "m16", "m10", "m17", "m11", "m18"]
  .map((n) => ({ src: `/assets/yt/mosaic/${n}.webp`, tall: +n.slice(1) >= 12 }));
const ROT = [-5, 3, -2, 6, -4, 2, 5, -6, 3, -3, 4, -1, 2, -4, 5, -2];
export function Mosaic() {
  return (
    <div className="relative columns-3 sm:columns-4 gap-2 sm:gap-3 py-4" aria-hidden="true" data-testid="andreea-mosaic">
      {MOSAIC.map(({ src, tall }, k) => (
        <img key={src} src={src} alt="" loading="lazy"
          className={`w-full mb-2 sm:mb-3 break-inside-avoid object-cover rounded-xl border-4 border-[var(--paper)] shadow-[0_14px_30px_-16px_rgba(0,0,0,.55)] hover:rotate-0 hover:scale-105 hover:relative hover:z-10 transition-transform duration-300 ${tall ? "aspect-[9/16]" : "aspect-video"}`}
          style={{ transform: `rotate(${ROT[k]}deg)` }} />
      ))}
    </div>
  );
}

export function SparkPortrait() {
  return (
    <div className="spark-portrait relative w-40 h-40 sm:w-48 sm:h-48 shrink-0" aria-hidden="true" data-testid="spark-portrait">
      <img src="/assets/v2/andreea-heliconia.webp" alt="" loading="lazy" className="w-full h-full rounded-full object-cover object-top border-2 border-[var(--gold)]" />
      {Array.from({ length: 14 }).map((_, k) => (
        <span key={k} className="spark" style={{ left: `${10 + ((k * 37) % 80)}%`, animationDelay: `${(k * 0.43) % 4}s`, animationDuration: `${3.2 + (k % 4) * 0.6}s`,
          background: k % 2 ? "var(--gold-light)" : "#9BE38F", color: k % 2 ? "#F3DDA6" : "#9BE38F" }} />
      ))}
    </div>
  );
}
